import { transition, trigger, useAnimation } from '@angular/animations';
import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Observable, Subscription, from } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AuthWrapperService } from '../../../../../core/auth';
import { GlobalMessageService, GlobalMessageType } from '../../../../../core/global-message';
import { PrincipalConfigurationService } from '../../../../../core/user';
import { shake } from '../../../../../shared/animations';
import { LoginFormView } from '../../model/login-form-view.model';

@Component({
  selector: 'py-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  animations: [trigger('shakeState', [transition('false <=> true', useAnimation(shake))])],
})
export class LoginFormComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  isLoading$ = new BehaviorSubject<boolean>(false);
  shakeState: boolean = false;
  loginFormView = LoginFormView;

  disableLogin$: Observable<boolean>;

  @Output()
  changeView = new EventEmitter<LoginFormView>();

  @Output()
  loginSuccess = new EventEmitter<boolean>();

  private subscriptions = new Subscription();

  constructor(
    private authWrapperService: AuthWrapperService,
    private changeDetectorRef: ChangeDetectorRef,
    private globalMessageService: GlobalMessageService,
    private fb: UntypedFormBuilder,
    private principalConfigurationService: PrincipalConfigurationService
  ) {}

  // Refactor each field to sub components as described in the Angular PRO course
  // This is essentially our container component here
  ngOnInit(): void {
    this.disableLogin$ = this.principalConfigurationService.isEnabled('enableLogin').pipe(map((enabled) => !enabled));

    this.form = this.fb.group({
      userId: ['', [Validators.required]],
      password: ['', [Validators.required]],
    });
    // Due to the inherited OnPush we have to mark this for checking if form status changes in order to pick up errors correctly
    this.subscriptions.add(
      this.form.statusChanges.subscribe(() => {
        this.changeDetectorRef.markForCheck();
      })
    );
  }

  login() {
    if (this.form.valid) {
      this.submitLogin();
    } else {
      this.shakeState = !this.shakeState;
      this.markFormAsTouched();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onResetInput(fieldName: string): void {
    this.form.get(fieldName).reset();
    this.form.get(fieldName).markAsDirty();
  }

  private submitLogin(): void {
    const { userId, password } = this.form.controls;
    this.isLoading$.next(true);
    this.subscriptions.add(
      from(this.authWrapperService.loginWithCredentials(userId.value.toLowerCase(), password.value))
        .pipe(take(1))
        .subscribe(
          (_response) => {
            // We want to remove error messages on successful login (primary the bad username/password combination)
            this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_ERROR);
            this.loginSuccess.emit(true);
            this.isLoading$.next(false);
          },
          (e) => {
            if (e.message !== 'unauthorized') {
              this.form.setErrors({ badCredentials: 'badCredentials' }); // Just a marker so the text does not matter
            }
            this.isLoading$.next(false);
            this.shakeState = !this.shakeState;
          }
        )
    );
  }

  private markFormAsTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.controls[key].markAsTouched();
    });
  }

  changeLoginPageView(view: LoginFormView): void {
    this.changeView.emit(view);
  }
}
